import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import i18nContext from 'components/i18n-context';
import { getErrorMessageForAlert } from 'services/utils';
import { isTokenValid } from 'services/authUtils';
import { DOCUMENT_REQUEST_STATUSES } from 'stores/documentRequestStore';
import StepForm from './StepForm';
import Confirmation from './Confirmation';
import InvalidLinkScreen from './InvalidLinkScreen';
import SuccessScreen from './SuccessScreen';
import './styles.scss';

const DocumentUpload = ({ documentRequestStore, userStore }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const i18n = useContext(i18nContext);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null); // 'INVALID_LINK' | 'SERVER_ERROR' | null
  // Which API surface (token vs authorized) the request was actually fetched with - must drive all
  // subsequent calls (upload/submit/download), not just "is the user logged in", since a logged-in
  // user can still be on a token link (email) that has to keep using the no-auth token API.
  const [isAuthorizedFlow, setIsAuthorizedFlow] = useState(false);

  // Parse id and token from URL hash or query params
  const getParamsFromUrl = () => {
    // First check hash fragment (preferred)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const idFromHash = hashParams.get('id');
    const tokenFromHash = hashParams.get('token');

    if (idFromHash && tokenFromHash) {
      return { id: idFromHash, token: tokenFromHash };
    }

    // Fallback to query params
    const searchParams = new URLSearchParams(window.location.search);
    return {
      id: searchParams.get('id'),
      token: searchParams.get('token')
    };
  };

  useEffect(() => {
    const initializeRequest = async () => {
      // Prevent multiple initializations
      if (isInitializing || isInitialized) {
        return;
      }

      const params = getParamsFromUrl();
      const requestId = params.id || id; // Use id from hash/query first, then from route params
      const token = params.token;

      const isUserAuthorized = isTokenValid();

      // Check if this is authorized flow (logged in user without token)
      const customerNumber = userStore?.userData?.account?.account_number;

      const isLoggedIn = isUserAuthorized && customerNumber;
      const isAuthorized = isLoggedIn && !token && requestId;

      if (!requestId) {
        setError('Invalid request link.');
        return;
      }

      // For authorized users, wait until customerNumber is available
      if (!token && isUserAuthorized) {
        if (!customerNumber) {
          // User is authorized but customerNumber not loaded yet - wait
          return;
        }
      } else if (!token && !isUserAuthorized) {
        // Check if userStore is still loading
        if (userStore?.isLoading) {
          return;
        }
        // Not authorized and no token - invalid
        setError('Invalid request link. Please use the link from the email or log in.');
        return;
      }

      setIsInitializing(true);

      try {
        if (isAuthorized) {
          // Authorized flow - use logged-in user's account
          documentRequestStore.requestId = requestId;

          // Fetch data using authorized API
          await documentRequestStore.fetchRequestById(customerNumber, requestId);
        } else {
          // Token flow - no-auth access
          documentRequestStore.initializeFromUrl(requestId, token);

          // Fetch real data from API
          await documentRequestStore.fetchRequestByToken(requestId, token);
        }
        setIsAuthorizedFlow(isAuthorized);
        setIsInitialized(true);
        setIsInitializing(false);
      } catch (err) {
        console.error('Failed to load document request:', err);
        setIsInitializing(false);

        // Check for various invalid/expired token scenarios
        const isInvalidLink =
          err?.response?.status === 401 ||
          err?.response?.status === 404 ||
          err?.code === 'INVALID_CREDENTIALS' ||
          err?.code === 'ACCESS_DENIED' ||
          err?.code === 'TOKEN_EXPIRED' ||
          err?.code === 'INVALID_TOKEN';

        if (isInvalidLink) {
          setErrorType('INVALID_LINK');
          setError('This document request link has expired or is invalid. Please contact support.');
        } else if (err?.response?.status >= 500) {
          setErrorType('SERVER_ERROR');
          const errorMessage = getErrorMessageForAlert(i18n, err, true, null, null);
          setError(errorMessage || 'Server error occurred. Please try again later.');
        } else {
          // For any other errors, also check if they might be link-related
          // If not sure, treat as invalid link to be safe
          setErrorType('INVALID_LINK');
          setError('This document request link has expired or is invalid. Please contact support.');
        }
      }
    };

    initializeRequest();
  }, [id, documentRequestStore, userStore, userStore?.userData?.account?.account_number]);

  // Cleanup on unmount only - a dependency array change (e.g. userStore.userData.account.account_number
  // resolving in the background) must NOT clear the just-fetched request data, since the effect above
  // re-runs on that same change and its cleanup would otherwise wipe documentRequestStore right after load.
  useEffect(() => {
    return () => {
      documentRequestStore.clear();
    };
  }, [documentRequestStore]);

  // Show InvalidLinkScreen only for invalid link errors - check this BEFORE loading state
  if (errorType === 'INVALID_LINK') {
    return <InvalidLinkScreen />;
  }

  // Show loading state
  if (documentRequestStore.isLoading && !isInitialized && !errorType) {
    return (
      <div className='document-upload-container'>
        <div className='loading-state'>
          <div className='spinner' />
          <p>Loading document request...</p>
        </div>
      </div>
    );
  }

  // Check if request is expired - use InvalidLinkScreen
  if (isInitialized && documentRequestStore.isExpired) {
    return <InvalidLinkScreen />;
  }

  // Check if request is already completed - use SuccessScreen with COMPLETED status
  if (isInitialized && documentRequestStore.status === DOCUMENT_REQUEST_STATUSES.COMPLETED) {
    return <SuccessScreen status={DOCUMENT_REQUEST_STATUSES.COMPLETED} />;
  }

  // Check if request is cancelled - use InvalidLinkScreen with CANCELLED status
  if (isInitialized && documentRequestStore.status === DOCUMENT_REQUEST_STATUSES.CANCELLED) {
    return <InvalidLinkScreen status={DOCUMENT_REQUEST_STATUSES.CANCELLED} />;
  }

  // Show success screen if request is already PROVIDED
  if (isInitialized && documentRequestStore.status === DOCUMENT_REQUEST_STATUSES.PROVIDED) {
    return <SuccessScreen status={DOCUMENT_REQUEST_STATUSES.PROVIDED} />;
  }

  // Show step form for REQUESTED status (with error if exists)
  if (isInitialized && documentRequestStore.status === DOCUMENT_REQUEST_STATUSES.REQUESTED) {
    return (
      <StepForm
        documentRequestStore={documentRequestStore}
        userStore={userStore}
        isAuthorizedFlow={isAuthorizedFlow}
        initialError={errorType === 'SERVER_ERROR' ? error : null}
      />
    );
  }

  // Show server error if exists and not initialized (only for true server errors like 500+)
  if (!isInitialized && errorType === 'SERVER_ERROR' && error) {
    return (
      <div className='document-upload-container'>
        <div className='error-state'>
          <h2>Server Error</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className='document-upload-container'>
      <div className='loading-state'>
        <div className='spinner' />
        <p>Initializing...</p>
      </div>
    </div>
  );
};

export default inject((stores) => ({
  documentRequestStore: stores.documentRequestStore,
  userStore: stores.userStore
}))(observer(DocumentUpload));
